import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@groot/shell/lib/api";

export interface PhotoRequestFile {
  id: number;
  photoRequestId: number;
  fileName: string;
  fileUrl: string;
  uploadedAt: string;
}

/** Maximum number of photos allowed per photo request (matches the server cap). */
export const MAX_PHOTOS_PER_REQUEST = 250;

export interface PhotoRequest {
  id: number;
  orderId: number;
  customerId: number;
  status: "pending" | "completed";
  publicLink: string;
  createdAt: string;
  completedAt: string | null;
  photosClearedAt?: string | null;
  photosClearedNote?: string | null;
  photosClearedCount?: number | null;
  /** Customer-facing print preview link (set once a print batch is shared). */
  previewLink?: string | null;
  previewSharedAt?: string | null;
  previewSubmittedAt?: string | null;
  order?: {
    id: number;
    orderNumber: string;
  };
  customer?: {
    id: number;
    name: string;
    mobileNumber: string;
  };
  files?: PhotoRequestFile[];
  _count?: {
    files: number;
  };
}

export interface CreatePhotoRequestDTO {
  orderId: number;
  customerId: number;
}

export interface UpdatePhotoRequestDTO {
  id: number;
  data: {
    status?: "pending" | "completed";
  };
}

export interface UploadPhotoDTO {
  photoRequestId: number;
  fileName: string;
  fileUrl: string;
}

export interface DuplicateFile {
  id: number;
  fileName: string;
  fileUrl: string;
  uploadedAt: string;
}

export interface DuplicateGroup {
  original: DuplicateFile;
  duplicates: DuplicateFile[];
  hammingDistance: number;
}

export interface DuplicateResult {
  groups: DuplicateGroup[];
  duplicateCount: number;
  scannedCount: number;
}

export interface ClearPhotosDTO {
  photoRequestId: number;
  note: string;
}

export function usePhotoRequests() {
  return useQuery({
    queryKey: ["photo-requests"],
    queryFn: async () => {
      return apiClient.get<PhotoRequest[]>("/photo-requests");
    },
  });
}

export function usePhotoRequest(id: number) {
  return useQuery({
    queryKey: ["photo-requests", id],
    queryFn: async () => {
      return apiClient.get<PhotoRequest>(`/photo-requests/${id}`);
    },
    enabled: !!id,
  });
}

export function usePhotoRequestByPublicLink(publicLink: string) {
  return useQuery({
    queryKey: ["photo-requests", "public", publicLink],
    queryFn: async () => {
      return apiClient.get<PhotoRequest>(`/public/photo-requests/${publicLink}`);
    },
    enabled: !!publicLink,
  });
}

export function useCreatePhotoRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePhotoRequestDTO) => {
      return apiClient.post<PhotoRequest>("/photo-requests", input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["photo-requests"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useUpdatePhotoRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: UpdatePhotoRequestDTO) => {
      return apiClient.put<PhotoRequest>(`/photo-requests/${id}`, data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["photo-requests"] });
      queryClient.invalidateQueries({ queryKey: ["photo-requests", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useDeletePhotoRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/photo-requests/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["photo-requests"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useAddPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ photoRequestId, fileName, fileUrl }: UploadPhotoDTO) => {
      return apiClient.post<PhotoRequestFile>(`/photo-requests/${photoRequestId}/files`, {
        fileName,
        fileUrl,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["photo-requests"] });
      queryClient.invalidateQueries({ queryKey: ["photo-requests", variables.photoRequestId] });
      queryClient.invalidateQueries({ queryKey: ["photo-requests", "public"] });
    },
  });
}

// Public upload — single request that uploads the file to S3 AND creates the
// photo record. Server resolves the photo request by publicLink. Used from
// the public photo upload page (no auth).
export function usePublicUploadPhoto(publicLink: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { file: File; contentType?: string }) => {
      const formData = new FormData();
      formData.append("file", params.file);
      return apiClient.postForm<PhotoRequestFile>(
        `/public/photo-requests/${publicLink}/files`,
        formData,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["photo-requests", "public", publicLink] });
    },
  });
}

// Public delete — removes a file by id, scoped to the publicLink. The server
// validates the file belongs to this publicLink before deleting.
export function usePublicDeletePhoto(publicLink: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ fileId }: { fileId: number }) => {
      await apiClient.delete(`/public/photo-requests/${publicLink}/files/${fileId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["photo-requests", "public", publicLink] });
    },
  });
}

export function useDeletePhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ photoRequestId, fileId }: { photoRequestId: number; fileId: number }) => {
      await apiClient.delete(`/photo-requests/${photoRequestId}/files/${fileId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["photo-requests"] });
      queryClient.invalidateQueries({ queryKey: ["photo-requests", variables.photoRequestId] });
      queryClient.invalidateQueries({ queryKey: ["photo-requests", "public"] });
    },
  });
}

export function useClearPhotos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ photoRequestId, note }: ClearPhotosDTO) => {
      return apiClient.post<PhotoRequest>(`/photo-requests/${photoRequestId}/clear-photos`, {
        note,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["photo-requests"] });
      queryClient.invalidateQueries({ queryKey: ["photo-requests", variables.photoRequestId] });
    },
  });
}

// Download all photos for a request as a single zip file.
export function useDownloadPhotoRequestZip() {
  return useMutation({
    mutationFn: async (photoRequestId: number) => {
      return apiClient.getBlob(`/photo-requests/${photoRequestId}/download-zip`);
    },
  });
}

// Find visually duplicate photos within a photo request. The server computes
// a perceptual hash (pHash) for each image and groups near-identical ones.
export function useFindDuplicates() {
  return useMutation({
    mutationFn: async (photoRequestId: number) => {
      return apiClient.get<DuplicateResult>(`/photo-requests/${photoRequestId}/duplicates`);
    },
  });
}
